import useIsMobile from 'hooks/useIsMobile';
import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getAnalyticsService } from 'services/analyticsService';

import type { Place } from '../../../@types';
import { IconType, ThemeType } from '../../../@types';
import { AppContext } from '../../../context/app';
import { useCancelSearchFocus } from '../../../hooks/useCancelSearchFocus';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import { usePlaceGroups } from '../../../hooks/usePlaceGroups';
import Icon from '../../shared/icon/Icon';
import SearchInput from '../../shared/searchInput/SearchInput';
import SearchState from '../../shared/searchState/SearchState';
import Spinner from '../../shared/spinner/Spinner';
import TextButton from '../../shared/textButton/TextButton';
import Title from '../../shared/title/Title';
import { SectionCategories } from '../sectionCategories/SectionCategories';
import SectionLocations from '../sectionLocations/SectionLocations';
import styles from './sectionGlobalSearch.module.scss';
import { PlaceSearchStateEnum } from './types';

interface SectionGlobalSearchProps {
  outsideClickRef?: React.RefObject<HTMLDivElement>;
}
const SectionGlobalSearch: React.FC<SectionGlobalSearchProps> = ({
  outsideClickRef,
}) => {
  const analyticsService = useMemo(() => getAnalyticsService(), []);
  const { t } = useTranslation('common');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const {
    maxLocationsItems,
    globalSearchText,
    setGlobalSearchText,
    setShowCTASection,
    setShowKeyboard,
    placeSearchState,
    setPlaceSearchState,
    selectedSite,
  } = useContext(AppContext);
  const { kiosk, kioskMode } = useKioskManager();
  const { placesData } = useNetworkManager();

  const { data: categories } = usePlaceGroups(
    process.env.REACT_APP_CUSTOMER_ENV!,
    selectedSite?.id,
    kiosk?.id,
  );

  const [debouncedSearchText, setDebouncedSearchText] = useState<
    string | undefined
  >(globalSearchText);

  const campusPlacesData = useMemo(
    () =>
      placesData?.filter((place) => place.location.site === selectedSite?.id),
    [placesData, selectedSite?.id],
  );

  const locationsBySearch = useMemo<Place[]>(() => {
    if (!debouncedSearchText) return [];

    const lowercaseSearchText = debouncedSearchText.toLowerCase();

    const matchesTag = (place: Place) =>
      place.tags.some((tag) => tag.toLowerCase().includes(lowercaseSearchText));

    const matchesName = (place: Place) =>
      place.name.toLowerCase().includes(lowercaseSearchText);

    const matchesShortName = (place: Place) =>
      place.short_name
        ? place.short_name.toLowerCase().includes(lowercaseSearchText)
        : false;

    const isSearchable = (place: Place) =>
      place.searchable === 'always' ||
      (kioskMode && place.searchable === 'onsite');

    const filteredPlaces = campusPlacesData?.filter(
      (place) =>
        (matchesTag(place) || matchesName(place) || matchesShortName(place)) &&
        isSearchable(place),
    );

    const results = filteredPlaces?.slice(0, maxLocationsItems) ?? [];

    analyticsService.track(analyticsService.events.GlobalCampusSearch, {
      value: debouncedSearchText,
      results: results.length,
    });

    return results;
  }, [
    analyticsService,
    debouncedSearchText,
    kioskMode,
    maxLocationsItems,
    campusPlacesData,
  ]);

  /*
    scroll to the end of the body
    avoid keyboard overlapping input on mobile
  */
  const searchComponentRef = useRef<HTMLDivElement>(null);
  const scrollIntoFocus = () => {
    if (searchComponentRef.current && isMobile) {
      searchComponentRef.current.scrollIntoView(true);
      // TODO: controlling scroll on android and ios is difficult here.
      //  there is a virtual keyboard on various heights on various dimensions with two different methods of handling scroll to focus
    }
  };

  const handleSearchFocus = () => {
    !globalSearchText &&
      setPlaceSearchState(PlaceSearchStateEnum.SEARCHLANDING);
    setShowKeyboard(true);
    scrollIntoFocus();
  };
  const handleCancelSearchFocus = useCancelSearchFocus();

  const handleClearSearchText = () => {
    setGlobalSearchText('');
  };

  useEffect(() => {
    placeSearchState === PlaceSearchStateEnum.SEARCHLANDING &&
      scrollIntoFocus();
  }, [placeSearchState]);

  useEffect(() => {
    if (placeSearchState === PlaceSearchStateEnum.DISPLAYCATEGORIES) {
      setShowCTASection(true);
      return;
    }
    setShowCTASection(false);

    if (!locationsBySearch) {
      if (debouncedSearchText !== undefined) {
        setPlaceSearchState(PlaceSearchStateEnum.SEARCHING);
      }
    }
  }, [
    locationsBySearch,
    debouncedSearchText,
    placeSearchState,
    setShowCTASection,
    setPlaceSearchState,
  ]);

  useEffect(() => {
    if (debouncedSearchText === undefined) {
      setPlaceSearchState(PlaceSearchStateEnum.DISPLAYCATEGORIES);
      return;
    }

    if (debouncedSearchText && locationsBySearch.length > 0) {
      setPlaceSearchState(PlaceSearchStateEnum.DISPLAYPLACES);
    } else if (debouncedSearchText && locationsBySearch.length === 0) {
      setPlaceSearchState(PlaceSearchStateEnum.NOTFOUND);
    } else {
      setPlaceSearchState(PlaceSearchStateEnum.SEARCHLANDING);
    }
  }, [debouncedSearchText, locationsBySearch.length, setPlaceSearchState]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(globalSearchText);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [globalSearchText]);

  const renderScreenSection = (state: PlaceSearchStateEnum) => {
    const sections = {
      [PlaceSearchStateEnum.DISPLAYCATEGORIES]: (
        <SectionCategories categories={categories ?? []} />
      ),
      [PlaceSearchStateEnum.CATAGORYFILTERED]: (
        <SectionCategories categories={categories ?? []} />
      ),
      [PlaceSearchStateEnum.DISPLAYPLACES]: (
        <div className={styles.searchResults}>
          <Title as="h2">{t('labels.searchResults')}</Title>
          <SectionLocations locations={locationsBySearch || []} />
        </div>
      ),
      [PlaceSearchStateEnum.SEARCHLANDING]: (
        <SearchState
          iconTop={
            <Icon
              icon={IconType.SEARCH}
              theme={ThemeType.PRIMARY}
              size="full"
            />
          }
          title={t('searchState.initial.title')}
          message={t('searchState.initial.message')}
        />
      ),
      [PlaceSearchStateEnum.SEARCHING]: (
        <SearchState
          iconTop={<Spinner theme={ThemeType.PRIMARY} iconSize={58} />}
          title={t('searchState.searching.title')}
          iconTitle={IconType.SEARCH}
          message={t('searchState.searching.message')}
        />
      ),
      [PlaceSearchStateEnum.NOTFOUND]: (
        <SearchState
          iconTop={
            <Icon
              icon={IconType.NO_RESULTS}
              theme={ThemeType.PRIMARY}
              size="full"
            />
          }
          title={t('searchState.notFound.title')}
          message={t('searchState.notFound.message')}
        />
      ),
    };
    return sections[state];
  };

  return (
    <section
      className={`${styles.section} ${
        kiosk && kioskMode && styles[kiosk.model]
      }`}
      ref={searchComponentRef}
    >
      <Title as="h2">{t('titles.globalCampusSearch')}</Title>
      <div className={styles.screenSearch}>
        <div className={styles.search}>
          <SearchInput
            value={globalSearchText || ''}
            onChange={(value) => setGlobalSearchText(value)}
            placeholder={t('placeholders.search')}
            onFocus={handleSearchFocus}
            onCancel={handleClearSearchText}
            outsideClickRef={outsideClickRef}
            searchInputRef={searchInputRef}
          />
          {placeSearchState !== PlaceSearchStateEnum.DISPLAYCATEGORIES && (
            <TextButton
              onClick={handleCancelSearchFocus}
              label={t('labels.cancel')}
              capitalize
              side="none"
            />
          )}
        </div>
        {renderScreenSection(placeSearchState)}
      </div>
    </section>
  );
};

export default memo(SectionGlobalSearch);
